// useGithubCode.ts
import { supabase, Database } from './supabase';
import { checkAndUpdateGithubToken } from './useGithubAccount';
import { GithubFormat, CodeFile } from '../pages/game/utils/formatCode';

export interface GithubSyncResult {
    status: 'SUCCESS' | 'ALREADY_SYNCED' | 'ERROR';
    message: string;
}

/**
 * Recursively retrieves all file contents from a GitHub repository, starting from a given path.
 *
 * @param apiUrl The base GitHub API URL for the contents.
 * @param accessToken The GitHub access token.
 * @param currentPath The current path being traversed (used for building full file paths).
 * @param files The array to accumulate CodeFile objects.
 */
async function fetchGithubDirectoryContents(
    apiUrl: string,
    accessToken: string,
    currentPath: string,
    files: CodeFile[]
) {
    // console.log(`Fetching from URL: ${apiUrl}`);

    const getResponse = await fetch(apiUrl, {
        headers: {
            'Authorization': `token ${accessToken}`,
            'Accept': 'application/vnd.github.v3+json'
        }
    });
    // console.log(`Status for ${apiUrl}: ${getResponse.status}`);

    if (getResponse.status === 200) {
        const contents = await getResponse.json();
        // console.log(`Raw contents for ${currentPath}:`, contents);

        if (!Array.isArray(contents)) {
            console.error(`Contents not an array for ${currentPath}:`, contents);
            throw new Error(`Unexpected response from GitHub API for ${currentPath} contents. Expected an array.`);
        }

        if (contents.length === 0) {
            // console.log(`Directory ${currentPath} is empty.`);
        }

        for (const item of contents) {
            const itemPath = item.path; // GitHub API provides full path
            // console.log(`Processing item: ${itemPath}, Type: ${item.type}`);

            if (item.type === 'file') {
                // IMPORTANT CHANGE: Make another API call to fetch the content of the file
                const fileContentResponse = await fetch(item.url, {
                    headers: {
                        'Authorization': `token ${accessToken}`,
                        'Accept': 'application/vnd.github.v3.raw' // Request raw content
                    }
                });

                if (fileContentResponse.ok) {
                    const decodedContent = await fileContentResponse.text(); // Get raw text content
                    files.push({
                        path: itemPath,
                        content: decodedContent,
                    });
                    // console.log(`Added file: ${itemPath}`);
                } else {
                    const errorData = await fileContentResponse.json();
                    console.error(`Error fetching content for file ${itemPath}:`, errorData);
                }
            } else if (item.type === 'dir') {
                // Recursively call for subdirectories
                await fetchGithubDirectoryContents(item.url, accessToken, itemPath, files);
            }
        }
    } else if (getResponse.status === 404) {
        // console.log(`Path '${currentPath}' not found. Skipping.`);
    } else {
        const errorData = await getResponse.json();
        console.error(`Error fetching directory contents from GitHub for ${currentPath}:`, errorData);
        throw new Error(`Failed to fetch directory contents from ${currentPath}: ${getResponse.status} - ${errorData.message || 'Unknown error'}`);
    }
}

/**
 * Retrieves the codebase content from a GitHub repository for a given game.
 * This function reads all files within the linked GitHub repository, recursively traversing directories.
 *
 * @param userId The UUID of the authenticated user.
 * @param repoName The name of the GitHub repository.
 * @param gameId The UUID of the game associated with the repository.
 * @returns A promise that resolves with GithubCode format, or rejects on error.
 * @throws Error if the operation fails due to missing GitHub account/repo info, network issues, or GitHub API errors.
 */
export async function getCodeFromGithub(
    userId: string,
    repoName: string,
    gameId: string,
): Promise<GithubFormat> {
    try {
        // 1. Fetch GitHub Account details and ensure token is valid
        const githubAccount = await checkAndUpdateGithubToken(userId);

        const accessToken = githubAccount.github_access_token;
        const githubUsername = githubAccount.github_username;

        const files: CodeFile[] = [];

        // 2. Get the contents from the root of the repository
        const apiUrl = `https://api.github.com/repos/${githubUsername}/${repoName}/contents/`; // Fetch from root
        // console.log(`Initial API URL: ${apiUrl}`);
        await fetchGithubDirectoryContents(apiUrl, accessToken, '', files); // Start recursive fetch from root

        // console.log(`Successfully retrieved code from GitHub for game ID ${gameId}. Found ${files.length} files.`);
        // console.log('Github code files array:', files);
        return files;

    } catch (err: any) {
        console.error('Error during getCodeFromGithub:', err);
        throw err;
    }
}

/**
 * Calls the Supabase Edge Function to sync code from GitHub to the database.
 *
 * @param userId The UUID of the authenticated user.
 * @param gameId The UUID of the game.
 * @returns A promise that resolves with the result of the sync operation.
 * @throws Error if the Edge Function call fails.
 */
export async function syncGithubCodeToDB(userId: string, gameId: string): Promise<GithubSyncResult> {
    try {
        const { data, error } = await supabase.functions.invoke('github-sync-code', {
            body: { userId, gameId, operation: 'syncGithubCodeToDB' },
        });

        if (error) {
            console.error('Error invoking syncGithubCodeToDB Edge Function:', error);
            return { status: 'ERROR', message: `Failed to sync code from GitHub to DB: ${error.message}` };
        }

        // console.log('Successfully invoked syncGithubCodeToDB Edge Function:', data);
        return JSON.parse(data);
    } catch (err: any) {
        console.error('Error during syncGithubCodeToDB:', err);
        return { status: 'ERROR', message: `An unexpected error occurred: ${err.message}` };
    }
}

/**
 * Calls the Supabase Edge Function to sync code from the database to GitHub.
 *
 * @param userId The UUID of the authenticated user.
 * @param gameId The UUID of the game.
 * @returns A promise that resolves with the result of the sync operation.
 * @throws Error if the Edge Function call fails.
 */
export async function syncDBCodeToGithub(userId: string, gameId: string): Promise<GithubSyncResult> {
    try {
        const { data, error } = await supabase.functions.invoke('github-sync-code', {
            body: { userId, gameId, operation: 'syncDBCodeToGithub' },
        });

        if (error) {
            console.error('Error invoking syncDBCodeToGithub Edge Function:', error);
            return { status: 'ERROR', message: `Failed to sync code from DB to GitHub: ${error.message}` };
        }

        // console.log('Successfully invoked syncDBCodeToGithub Edge Function:', data);
        return JSON.parse(data);
    } catch (err: any) {
        console.error('Error during syncDBCodeToGithub:', err);
        return { status: 'ERROR', message: `An unexpected error occurred: ${err.message}` };
    }
}